import React, { useState, useMemo, useCallback, useEffect, } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button, DatePicker, Select, Row, Col, Modal } from 'antd';
import {
  PrinterOutlined,
  RetweetOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { parseDate } from '../../../utils/dateUtils';

import Table from '../../../core/pagination/datatable';
import CommonFooter from '../../../core/common/footer/commonFooter';
import Tag from '../../../core/common/tag';
import { useShopId } from '../../../hooks/useShopId';
import { formatPrice, parsePrice } from '../../../utils/priceUtils';

// thunks
import { fetchPaymentsByDateRange, refundPayment } from '../../../core/redux/payment.slice';
import { fetchPaymentMethods } from '../../../core/redux/paymentMethod.slice';
import {
  setIncomePaymentsDateRange,
  setIncomePaymentsReference,
  setIncomePaymentsCustomerName,
  setIncomePaymentsPaymentMode,
  setIncomePaymentsStatus,
  setIncomePaymentsPagination,
} from '../../../core/redux/incomePaymentsFilters.slice';
import StickyBar from '../../../core/common/StickyBar';
import LoadingState from '../../../core/common/LoadingState';
import { usePrintContext } from '../../../core/utils/printContext';
import { useCurrency } from '../../../hooks/useCurrency'

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const IncomeList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
     const currency = useCurrency();
  
  const { shopId, isAllShops } = useShopId();
  const { payments, loading } = useSelector((state) => state.payment);
  const { paymentMethods } = useSelector((state) => state.paymentMethod);

  const todayStart = useMemo(() => dayjs().startOf('day'), []);
  const todayEnd = useMemo(() => dayjs().endOf('day'), []);
  const { getPdfColor } = usePrintContext();

  const {
    dateRange: dateRangeFilter,
    reference,
    customerName,
    paymentModeId,
    status,
    pagination,
  } = useSelector((state) => state.incomePaymentsFilters);
  const [customerInput, setCustomerInput] = useState(customerName || '');
  useEffect(() => {
    if (!dateRangeFilter?.[0] || !dateRangeFilter?.[1]) {
      dispatch(
        setIncomePaymentsDateRange([
          todayStart.toISOString(),
          todayEnd.toISOString(),
        ])
      );
    }
  }, [dispatch, dateRangeFilter, todayStart, todayEnd]);

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  useEffect(() => {
    const [startIso, endIso] = dateRangeFilter || [];
    if (!startIso || !endIso) return;

    dispatch(
      fetchPaymentsByDateRange({
        startDate: startIso,
        endDate: endIso,
      })
    );
  }, [dispatch, dateRangeFilter]);

  useEffect(() => {
    setCustomerInput(customerName || '');
  }, [customerName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setIncomePaymentsCustomerName(customerInput));
    }, 300);

    return () => clearTimeout(timer);
  }, [customerInput, dispatch]);
  const dataSource = useMemo(() => payments || [], [payments]);

  const paymentMethodById = useMemo(() => {
    const map = new Map();
    (paymentMethods || []).forEach((pm) => {
      map.set(pm.paymentMethodId, pm.paymentMethodName || pm.name);
    });
    return map;
  }, [paymentMethods]);

  const paymentModes = useMemo(() => {
    return (paymentMethods || []).map((pm) => ({
      id: pm.paymentMethodId,
      label: pm.paymentMethodName || pm.name,
    }));
  }, [paymentMethods]);

  const getPaymentStatusMeta = useCallback((rawStatus) => {
    const value = String(rawStatus || '').toLowerCase().trim();

    if (!value) {
      return {
        description: t('income.status.active'),
        color: 'success',
      };
    }

    if (value.includes(t("income.status.pendingRefund"))) {
      return {
        description: t('income.status.pendingRefund'),
        color: 'warning',
      };
    }

    if (value.includes('rembours')) {
      return {
        description: t('income.status.refunded'),
        color: 'danger',
      };
    }

    if (value.includes('annul')) {
      return {
        description: t('income.status.cancelled'),
        color: 'danger',
      };
    }

    if (value.includes('active') || value.includes('actif')) {
      return {
        description: t('income.status.active'),
        color: 'success',
      };
    }

    return {
      description: rawStatus || t('income.status.unknown'),
      color: 'secondary',
    };
  }, [t]);

  const getReferenceValue = useCallback((item) => {
    return (
      item.reference ||
      item.Reference ||
      item.saleNumber ||
      item.orderNumber ||
      item.invoiceNumber ||
      ''
    )
      .toString()
      .trim();
  }, []);

  const getCustomerNameValue = useCallback((item) => {
    return (item.customerName || '').toString().trim();
  }, []);

  const getStatusValue = useCallback(
    (item) => {
      const rawStatus =
        item.statusName || item.statusDescription || item.status || '';
      return getPaymentStatusMeta(rawStatus).description;
    },
    [getPaymentStatusMeta]
  );

  const paymentStatusOptions = useMemo(() => {
    const statuses = (dataSource || []).map((item) => getStatusValue(item));
    return [...new Set(statuses)].filter(Boolean);
  }, [dataSource, getStatusValue]);

  const handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      dispatch(setIncomePaymentsDateRange([null, null]));
      return;
    }

    dispatch(
      setIncomePaymentsDateRange([
        dates[0].startOf('day').toISOString(),
        dates[1].endOf('day').toISOString(),
      ])
    );
  };

  const filteredPayments = useMemo(() => {
    let filtered = dataSource;

    if (dateRangeFilter?.[0] && dateRangeFilter?.[1]) {
      const start = dayjs(dateRangeFilter[0]).startOf('day');
      const end = dayjs(dateRangeFilter[1]).endOf('day');

      filtered = filtered.filter((item) => {
        const rowDate = parseDate(item.paymentDate || item.date);
        if (!rowDate) return false;
        return rowDate.isSameOrAfter(start) && rowDate.isSameOrBefore(end);
      });
    }

    if (!isAllShops && shopId) {
      filtered = filtered.filter((item) => {
        return String(item.shopId) === String(shopId);
      });
    }

    if (reference?.trim()) {
      const referenceFilter = reference.trim().toLowerCase();

      filtered = filtered.filter((item) => {
        const referenceValue = getReferenceValue(item).toLowerCase();
        return referenceValue.includes(referenceFilter);
      });
    }

    if (customerName?.trim()) {
      const customerFilter = customerName.trim().toLowerCase();

      filtered = filtered.filter((item) => {
        const customerValue = getCustomerNameValue(item).toLowerCase();
        return customerValue.includes(customerFilter);
      });
    }

    if (paymentModeId) {
      filtered = filtered.filter(
        (item) => item.paymentMethodId === paymentModeId
      );
    }

    if (status) {
      filtered = filtered.filter((item) => getStatusValue(item) === status);
    }

    return filtered;
  }, [
    dataSource,
    dateRangeFilter,
    isAllShops,
    shopId,
    reference,
    customerName,
    paymentModeId,
    status,
    getReferenceValue,
    getCustomerNameValue,
    getStatusValue,

  ]);

  const {
    totalActiveAmount,
    totalRefunded,
    totalPendingRefund,
  } = useMemo(() => {
    let totalActive = 0;
    let refunded = 0;
    let pending = 0;

    filteredPayments.forEach((item) => {
      const description = getStatusValue(item);
      const amount = parsePrice(item.paymentAmountIncludingTax ?? item.Amount);
      const isActive = item.paymentIsActive ?? item.isActive ?? true;

      if (isActive) {
        totalActive += amount;
      }

      if (description.toLowerCase() === t('income.status.refunded')) {
        refunded += amount;
      }

      if (
        description.toLowerCase() === t('income.status.pendingRefund') &&
        isActive
      ) {
        pending += amount;
      }
    });

    return {
      totalActiveAmount: totalActive,
      totalRefunded: refunded,
      totalPendingRefund: pending,
    };
  }, [filteredPayments, getStatusValue,t]);

  const handlePrint = useCallback(() => {
    const doc = new jsPDF();

    if (!filteredPayments.length) {
      doc.setFontSize(12);
      doc.text(t('income.noPayments'), 14, 20);

      const pdfUrl = doc.output('bloburl');
      window.open(pdfUrl, '_blank');
      return;
    }

    doc.setFontSize(16);
    doc.text(t('income.printTitle'), 14, 15);
    doc.setFontSize(10);
    doc.text(
      t('income.printDate', { date: new Date().toLocaleDateString('fr-FR') }),
      14,
      22
    );

    const tableData = filteredPayments.map((payment) => {
      const paymentMethod =
        paymentMethodById.get(payment.paymentMethodId) ||
        payment.paymentMethodName ||
        '-';

      return [
        payment.shopName || '-',
        parseDate(payment.paymentDate || payment.date)?.format('DD/MM/YYYY HH:mm') || '-',
        getReferenceValue(payment) || '-',
        getCustomerNameValue(payment) || '-',
        paymentMethod,
        getStatusValue(payment),
        `${formatPrice(payment.paymentAmountIncludingTax ?? payment.Amount)} ${currency.symbol}`,
      ];
    });

    autoTable(doc, {
      head: [[
        t('income.table.store'),
        t('income.table.date'),
        t('income.table.reference'),
        t('income.table.customer'),
        t('income.table.paymentMode'),
        t('income.table.status'),
        t('income.table.amount'),
      ]],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: getPdfColor("primary"),
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        6: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    const pdfUrl = doc.output('bloburl');
    window.open(pdfUrl, '_blank');
  }, [
    filteredPayments,
    paymentMethodById,
    getReferenceValue,
    getCustomerNameValue,
    getStatusValue,
    getPdfColor,
    t,currency
  ]);

  const handleRefund = useCallback(
    (payment) => {
      dispatch(refundPayment(payment.paymentId));
    },
    [dispatch]
  );

  const showRefundConfirm = useCallback(
    (payment) => {
      Modal.confirm({
        title: t('income.refundModal.title'),
        icon: <ExclamationCircleOutlined />,
        content: t('income.refundModal.message'),
        okText: t('income.refundModal.confirm'),
        cancelText: t('common.cancel'),
        okType: 'primary',
        centered: true,
        onOk: () => handleRefund(payment),
      });
    },
    [handleRefund,t]
  );

  const columns = useMemo(
    () => [
      {
        title: t('income.table.store'),
        dataIndex: 'shopName',
        key: 'store',
        render: (_, record) => record.shopName || '-',
        sorter: (a, b) =>
          (a.shopName || a.store || a.Store || '').localeCompare(
            b.shopName || b.store || b.Store || ''
          ),
      },
      {
        title: t('income.table.date'),
        dataIndex: 'paymentDate',
        key: 'date',
        render: (_, record) =>
          parseDate(record.paymentDate || record.date)?.format('DD/MM/YYYY HH:mm') || '',
        sorter: (a, b) =>
          dayjs(a.paymentDate || a.date).valueOf() -
          dayjs(b.paymentDate || b.date).valueOf(),
      },
      {
        title: t('income.table.reference'),
        dataIndex: 'reference',
        key: 'reference',
        render: (_, record) => getReferenceValue(record) || '-',
        sorter: (a, b) =>
          getReferenceValue(a).localeCompare(getReferenceValue(b)),
      },
      {
        title: t('income.table.customer'),
        dataIndex: 'customerName',
        key: 'customerName',
        render: (_, record) => getCustomerNameValue(record) || '-',
        sorter: (a, b) =>
          getCustomerNameValue(a).localeCompare(getCustomerNameValue(b)),
      },
      {
        title: t('income.table.paymentMode'),
        dataIndex: 'paymentMethodId',
        key: 'paymentMethod',
        render: (_, record) =>
          paymentMethodById.get(record.paymentMethodId) ||
          record.paymentMethodName ||
          '-',
        sorter: (a, b) => {
          const labelA =
            paymentMethodById.get(a.paymentMethodId) || a.paymentMethodName || '';
          const labelB =
            paymentMethodById.get(b.paymentMethodId) || b.paymentMethodName || '';
          return labelA.localeCompare(labelB);
        },
      },
      {
        title: t('income.table.status'),
        dataIndex: 'statusName',
        key: 'status',
        render: (_, record) => {
          const rawStatus =
            record.statusName || record.statusDescription || record.status || '';
          const meta = getPaymentStatusMeta(rawStatus);

          return (
            <Tag
              color={meta.color}
              content={meta.description}
              size="sm"
              showDot
            />
          );
        },
        sorter: (a, b) =>
          getStatusValue(a).localeCompare(getStatusValue(b)),
      },
      {
        title: t('income.table.amount'),
        dataIndex: 'paymentAmountIncludingTax',
        key: 'amount',
        align: 'right',
        render: (_, record) => (
          <div className="text-end">
            <strong>{formatPrice(record.paymentAmountIncludingTax ?? record.Amount)}</strong>
          </div>
        ),
        sorter: (a, b) =>
          parsePrice(a.paymentAmountIncludingTax ?? a.Amount) -
          parsePrice(b.paymentAmountIncludingTax ?? b.Amount),
      },
      {
        title: t('income.table.action'),
        key: 'action',
        align: 'center',
        render: (_, record) => {
          const canRefund =
            getStatusValue(record).toLowerCase() ===
            'en attente de remboursement';

          if (!canRefund) {
            return <span>-</span>;
          }

          return (
            <Button
              type="link"
              className="action-set"
              size="large"
              onClick={() => showRefundConfirm(record)}
              style={{ padding: 0, height: 'auto' }}
            >
              <RetweetOutlined style={{ fontSize: 28 }} />
            </Button>
          );
        },
      },
    ],
    [
      paymentMethodById,
      getReferenceValue,
      getCustomerNameValue,
      getPaymentStatusMeta,
      getStatusValue,
      showRefundConfirm,
      t
    ]
  );

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">


          <StickyBar
            title={t('income.title')}
            count={filteredPayments.length}
            actions={
              <Button onClick={handlePrint} className="btn-customized-primary" >
                <PrinterOutlined />
                {t('income.print')}
              </Button>
            }
          />
          <div className="row">
            <div className="col-xl-4 col-sm-6 col-12 d-flex">
              <div className="card dash-widget w-100">
                <div className="card-body">
                  <h5 className="text-muted mb-2">{t('income.metrics.totalActive')}</h5>
                  <h4 className="mb-0">{formatPrice(totalActiveAmount)}</h4>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-sm-6 col-12 d-flex">
              <div className="card dash-widget dash1 w-100">
                <div className="card-body">
                  <h5 className="text-muted mb-2">{t('income.metrics.totalRefunded')}</h5>
                  <h4 className="mb-0">{formatPrice(totalRefunded)}</h4>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-sm-6 col-12 d-flex">
              <div className="card dash-widget dash2 w-100">
                <div className="card-body">
                  <h5 className="text-muted mb-2">{t('income.metrics.pendingRefund')}</h5>
                  <h4 className="mb-0">{formatPrice(totalPendingRefund)}</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="card table-list-card manage-stock">
            <div className="card-header">
              <div className="card-body pb-0">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={7}>
                    <div className="input-blocks">
                      <label>{t('income.filters.period')}</label>
                      <RangePicker
                        value={[
                          dateRangeFilter?.[0] ? dayjs(dateRangeFilter[0]) : null,
                          dateRangeFilter?.[1] ? dayjs(dateRangeFilter[1]) : null,
                        ]}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        placeholder={[t('income.filters.periodStart'), t('income.filters.periodEnd')]}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={4}>
                    <div className="input-blocks">
                      <label>{t('income.filters.reference')}</label>
                      <Search
                        placeholder={t('income.filters.referencePlaceholder')}
                        allowClear
                        value={reference}
                        onChange={(e) =>
                          dispatch(setIncomePaymentsReference(e.target.value))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={4}>
                    <div className="input-blocks">
                      <label>{t('income.filters.customer')}</label>
                      <Search
                        placeholder={t('income.filters.customerPlaceholder')} allowClear
                        value={customerInput}
                        onChange={(e) => setCustomerInput(e.target.value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={4}>
                    <div className="input-blocks">
                      <label>{t('income.filters.paymentMode')}</label>
                      <Select
                        placeholder={t('income.filters.paymentModePlaceholder')}
                        allowClear
                        value={paymentModeId || undefined}
                        onChange={(value) =>
                          dispatch(setIncomePaymentsPaymentMode(value ?? null))
                        }
                        style={{ width: '100%' }}
                        popupMatchSelectWidth={false}
                      >
                        {paymentModes.map((mode) => (
                          <Option key={mode.id} value={mode.id}>
                            {mode.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={5}>
                    <div className="input-blocks">
                      <label>{t('income.filters.status')}</label>
                      <Select
                        placeholder={t('income.filters.statusPlaceholder')}
                        allowClear
                        value={status || undefined}
                        onChange={(value) =>
                          dispatch(setIncomePaymentsStatus(value ?? null))
                        }
                        style={{ width: '100%' }}
                        popupMatchSelectWidth={false}
                      >
                        {paymentStatusOptions.map((statusLabel) => (
                          <Option key={statusLabel} value={statusLabel}>
                            {statusLabel}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <LoadingState type="table" />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={filteredPayments}
                    rowKey={(record) => record?.paymentId}
                    currentPage={pagination.current}
                    pageSize={pagination.pageSize}
                    onPageChange={(page, pageSize) => {
                      dispatch(
                        setIncomePaymentsPagination({
                          current: page,
                          pageSize,
                        })
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </div>
  );
};

export default IncomeList;